




































import { computed, defineComponent, onBeforeUnmount, PropType, ref, watch } from "@vue/composition-api";
import { CollabLiveChat } from "@scrile/api-provider/dist/api/CollabLiveChatsProvider";
import { LivechatSubjectType } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { $t } from "@/lib/i18nHelpers";
import { parseISO } from "@/lib/time";
import { getLiveUrl } from "@/lib/urlHelpers";
import { sendJoinNotification } from "@/views/PageProfile/components/StreamControllers/helpers";
import StreamView from "../../../components/StreamView";
import useConsume from "../../../hooks/useConsume";
import useUserBroadcastingUpdated from "../../../hooks/useUserBroadcastingUpdated";
import useCollabLiveChat from "../../hooks/useCollabLiveChat";
import LeaveCollabModal from "../LeaveCollabModal";

export default defineComponent({
  name: "ConsumeController",
  components: { LeaveCollabModal, StreamView },
  props: {
    user: { type: Object as PropType<UserPublicData>, required: true },
    collabLive: { type: Object as PropType<CollabLiveChat>, required: true },
  },

  setup(props, { root, emit }) {
    const { isAdmin } = useAuthUser();
    const {
      loading,
      joinData,
      userList,
      clientServerTimeDiff,
      errorData,
      referrersMap,
      isBlockedForSession,
      endChat,
      endEntireCollabLive,
    } = useCollabLiveChat({ chatId: props.collabLive.id, userId: props.user.id, referrerId: props.user.id });

    const getLoggerContext = (): Record<string, any> => {
      return {
        joinData: joinData.value,
        errorData: errorData.value,
      };
    };

    const {
      usersToConsume,
      connecting: consumeConnecting,
      connectingState: consumeConnectingState,
      consumeStream,
      removeUser,
      closeAllStreams,
    } = useConsume({ joinData, getLoggerContext });

    let timerToExit: ReturnType<typeof setTimeout> | null;

    useUserBroadcastingUpdated(props.user.id, data => {
      if (!data.broadcasting && data.subject.subjectType === LivechatSubjectType.COLLAB_LIVE) {
        timerToExit = setTimeout(() => emit("close"), 5000);
      }
      if (data.broadcasting && data.subject.subjectType === LivechatSubjectType.PUBLIC_LIVECHAT) {
        timerToExit && clearTimeout(timerToExit);
        root.$router.replace(getLiveUrl(props.user));
      }
    });

    const showEndConfirm = ref(false);

    const connecting = computed(() => Object.values(consumeConnecting.value)[0] ?? false);
    const connectingState = computed(() => Object.values(consumeConnectingState.value)[0] ?? "");

    const producer = computed(() => joinData.value?.users.find(i => i.userId === props.user.id) ?? null);

    const dateStart = computed(() => {
      if (!producer.value) {
        return "";
      }
      return new Date(parseISO(producer.value.joinTime).getTime() + clientServerTimeDiff.value).toISOString();
    });

    const menuItems = computed(() =>
      isAdmin.value
        ? [
            {
              name: "exitCollab",
              text: $t("Exit live"),
            },
            {
              name: "endChat",
              text: $t("End"),
              class: "negative",
            },
          ]
        : []
    );

    const blockedCloseStream = async () => {
      isBlockedForSession.value = true;
      closeAllStreams();
      await endChat();
    };

    const onEndCollab = async () => {
      if (isAdmin.value) {
        showEndConfirm.value = true;
      } else {
        emit("close");
      }
    };

    const onDoEnd = async (cb: () => void) => {
      try {
        await endEntireCollabLive();
        showEndConfirm.value = false;
      } finally {
        cb();
      }
    };

    watch(joinData, (joinData, old) => {
      if (!joinData) return;
      const streamingUsers = joinData.users.filter(i => i.broadcasting && i.id !== joinData.me.id);
      const streamingUserIds = streamingUsers.map(i => i.id);
      const oldStreamingUserIds = (old?.users.filter(i => i.broadcasting && i.id !== old.me.id) ?? []).map(i => i.id);

      const idsToConsume = streamingUserIds.filter(id => !oldStreamingUserIds.includes(id));
      idsToConsume.forEach(id => {
        consumeStream(id, joinData.token);
        const user = streamingUsers.find(i => i.id === id);
        old && user?.user && sendJoinNotification(user?.user);
      });
      const idsToRemove = oldStreamingUserIds.filter(id => !streamingUserIds.includes(id));
      idsToRemove.forEach(id => removeUser(id));
    });

    watch(errorData, error => {
      if (!error) return;
      emit("error", error);
    });

    onBeforeUnmount(() => {
      timerToExit && clearTimeout(timerToExit);
    });

    return {
      loading,
      joinData,
      userList,
      usersToConsume,
      connecting,
      connectingState,
      dateStart,
      menuItems,
      showEndConfirm,
      isAdmin,
      referrersMap,
      isBlockedForSession,
      blockedCloseStream,
      onEndCollab,
      onDoEnd,
    };
  },
});
